import { openURL } from 'expo-linking'
import debounce from 'lodash.debounce'
import { useCallback, useImperativeHandle, useState, type ReactNode } from 'react'
import { Platform, View } from 'react-native'

import { CopyButton } from '@/src/elements/button/copy/CopyButton'
import { Button } from '@/src/elements/button/default'
import { Modal } from '@/src/elements/modal/Modal'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { getIsMobile } from '@/src/general/utils/getIsMobile'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, type TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TShareDataGetter } from '../../../models/share.types'
import { getWhatsAppUrl } from '../../../utils/getWhatsAppUrl'

type TShareDetails = {
  url: string
  onShareMail: VoidFunction
  onShareWhatsApp: VoidFunction
  onCancel: VoidFunction
}

export type TShareModalProps = {
  onLoadingChange?: (isLoading: boolean) => void
  title: string
  getShareData: TShareDataGetter
  description: ReactNode
  trackingSource: TRACKING_SOURCE
  children?: (props: { isLoading: boolean; onShare: VoidFunction }) => ReactNode
  ref?: React.Ref<{ onShare: () => void }>
}

export const ShareModal = ({
  children,
  title: modalTitle,
  description,
  getShareData,
  trackingSource,
  onLoadingChange,
  ref,
}: TShareModalProps): ReactNode => {
  const [isLoading, setIsLoading] = useState(false)
  const [shareDetails, setShareDetails] = useState<TShareDetails | undefined>()
  const { t } = useTranslate()

  /**
   * Get share data from the api through the getShareData prop, then:
   * 1. try the navigator share api
   * 2. or use the modal as a fallback by setting the shareDetails state
   */
  const onShare = useCallback(async () => {
    onLoadingChange?.(true)
    setIsLoading(true)
    const shareData = await getShareData()
    onLoadingChange?.(false)
    setIsLoading(false)

    const { text, title = modalTitle, url } = shareData

    const fallbackShare = () => {
      const mailTo = `mailto:?body=${encodeURIComponent(text)}`

      const handleEmail = () => {
        GTM.sendEvent({ event: EVENTS.PARTAGE_MAILTO }, { trackingSource })
        if (Platform.OS === 'web') {
          window.open(mailTo, '_blank')
        } else {
          openURL(mailTo)
        }
      }

      const whatsAppUrl = getWhatsAppUrl(text)

      const onShareWhatsApp = () => {
        GTM.sendEvent({ event: EVENTS.PARTAGE_WHATSAPP }, { trackingSource })
        if (Platform.OS === 'web') {
          window.open(whatsAppUrl, '_blank')
        } else {
          openURL(whatsAppUrl)
        }
      }

      setShareDetails({
        url,
        onShareMail: debounce(handleEmail, 500, { leading: true, trailing: false }),
        onShareWhatsApp,
        onCancel: () => setShareDetails(undefined),
      })
    }

    if (typeof navigator === 'object' && navigator.share != null && !getIsMobile()) {
      fallbackShare()
    } else {
      try {
        await navigator.share({ title, text })
      } catch (_) {
        fallbackShare()
      }
    }
  }, [getShareData, modalTitle, trackingSource, onLoadingChange])

  useImperativeHandle(ref, () => ({
    onShare,
  }))

  const handleCopy = () => GTM.sendEvent({ event: EVENTS.PARTAGE_COPY }, { trackingSource })

  return (
    <>
      {children && children({ isLoading, onShare })}
      <Modal
        showModal={!!shareDetails}
        onCancel={shareDetails?.onCancel}
        title={modalTitle}
        testID={TEST_ID.MODAL.SHARE}
        classNames={{
          base: 'bg-white',
        }}
      >
        <View className="flex w-full gap-6">
          {description}
          <Button
            className="w-full bg-social-whatsapp-600 enabled:hover:bg-social-whatsapp-700"
            startIcon={ICON.WHATSAPP}
            variant="solid"
            colors="default"
            testID={TEST_ID.INTENTION.SHARE.WHATSAPP}
            onPress={shareDetails?.onShareWhatsApp}
          >
            {t('share.wording.share-on-whatsapp')}
          </Button>
          {shareDetails && (
            <CopyButton
              fullWidth
              onPress={handleCopy}
              testID={TEST_ID.INTENTION.SHARE.COPY}
              link={shareDetails.url}
              className="w-full"
            />
          )}

          <Button
            colors="secondary"
            variant="solid"
            className="w-full"
            startIcon={ICON.MAIL}
            testID={TEST_ID.INTENTION.SHARE.EMAIL}
            onPress={shareDetails?.onShareMail}
          >
            {t('share.wording.send-email')}
          </Button>
        </View>
      </Modal>
    </>
  )
}
